import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import { addToCart, getCartItems } from '../services/cartService';
import { Product, getAllProducts } from '../services/productService';
import { useAuth } from '../contexts/AuthContext';

const FeaturedSlider: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const [addedToCart, setAddedToCart] = useState<number[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const autoplayTimerRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Fetch featured products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const products = await getAllProducts();

        // Use placeholder data if API returns empty products
        if (!products || products.length === 0) {
          const placeholderProducts: Product[] = [
            {
              id: 1,
              title: "Premium Fantasy Dungeon Pack",
              description: "Complete 3D dungeon package with animated props and lighting effects",
              price: 149.99,
              images: ["https://images.unsplash.com/photo-1560419015-7c427e8ae5ba?q=80&w=1470&auto=format&fit=crop"],
              badges: ["3D Models", "Environments", "Best Seller"],
              category: "3D Assets",
              tags: ["3D Models", "Environments", "Fantasy", "Dungeon"],
              sellerId: "seller123",
              sellerName: "PixelWizard",
              rating: 4.8,
              releaseDate: "2023-05-12",
              likes: 2450,
              downloads: 1800,
              features: ["High quality textures", "PBR materials", "Optimized meshes", "Animated props"]
            },
            {
              id: 2,
              title: "Sci-Fi UI Interface Kit",
              description: "Futuristic UI elements and HUD components for space-themed games",
              price: 79.99,
              images: ["https://images.unsplash.com/photo-1548391350-968f58dedaed?q=80&w=1587&auto=format&fit=crop"],
              badges: ["UI Kits", "2D Assets", "New"],
              category: "UI Kits",
              tags: ["UI", "Interface", "Sci-Fi", "HUD"],
              sellerId: "seller456",
              sellerName: "UIExpert",
              rating: 4.6,
              releaseDate: "2023-06-20",
              likes: 1830,
              downloads: 1200,
              features: ["Vector shapes", "Layered PSD files", "Customizable elements", "Multiple color schemes"]
            },
            {
              id: 3,
              title: "Advanced Character Controller",
              description: "Highly optimized character movement system with advanced physics",
              price: 59.99,
              images: ["https://images.unsplash.com/photo-1550745165-9bc0b252726f?q=80&w=1470&auto=format&fit=crop"],
              badges: ["Scripts", "Physics", "Editor's Choice"],
              category: "Scripts",
              tags: ["Character Controller", "Physics", "Movement", "Animation"],
              sellerId: "seller789",
              sellerName: "CodeMaster",
              rating: 4.9,
              releaseDate: "2023-04-05",
              likes: 2180,
              downloads: 1650,
              features: ["Advanced physics", "Animation integration", "Input handling", "Cross-platform support"]
            }
          ];
          setFeaturedProducts(placeholderProducts);
        } else {
          // Get featured products or most popular ones (top 3)
          const featured = products
            .sort((a, b) => b.downloads - a.downloads) // Sort by popularity
            .slice(0, 3); // Take top 3

          setFeaturedProducts(featured);
        }
      } catch (err) {
        console.error('Error fetching featured products:', err);
        // Use placeholder data instead of showing an error
        const placeholderProducts: Product[] = [
          {
            id: 1,
            title: "Premium Fantasy Dungeon Pack",
            description: "Complete 3D dungeon package with animated props and lighting effects",
            price: 149.99,
            images: ["https://images.unsplash.com/photo-1560419015-7c427e8ae5ba?q=80&w=1470&auto=format&fit=crop"],
            badges: ["3D Models", "Environments", "Best Seller"],
            category: "3D Assets",
            tags: ["3D Models", "Environments", "Fantasy", "Dungeon"],
            sellerId: "seller123",
            sellerName: "PixelWizard",
            rating: 4.8,
            releaseDate: "2023-05-12",
            likes: 2450,
            downloads: 1800,
            features: ["High quality textures", "PBR materials", "Optimized meshes", "Animated props"]
          },
          {
            id: 2,
            title: "Sci-Fi UI Interface Kit",
            description: "Futuristic UI elements and HUD components for space-themed games",
            price: 79.99,
            images: ["https://images.unsplash.com/photo-1548391350-968f58dedaed?q=80&w=1587&auto=format&fit=crop"],
            badges: ["UI Kits", "2D Assets", "New"],
            category: "UI Kits",
            tags: ["UI", "Interface", "Sci-Fi", "HUD"],
            sellerId: "seller456",
            sellerName: "UIExpert",
            rating: 4.6,
            releaseDate: "2023-06-20",
            likes: 1830,
            downloads: 1200,
            features: ["Vector shapes", "Layered PSD files", "Customizable elements", "Multiple color schemes"]
          },
          {
            id: 3,
            title: "Advanced Character Controller",
            description: "Highly optimized character movement system with advanced physics",
            price: 59.99,
            images: ["https://images.unsplash.com/photo-1550745165-9bc0b252726f?q=80&w=1470&auto=format&fit=crop"],
            badges: ["Scripts", "Physics", "Editor's Choice"],
            category: "Scripts",
            tags: ["Character Controller", "Physics", "Movement", "Animation"],
            sellerId: "seller789",
            sellerName: "CodeMaster",
            rating: 4.9,
            releaseDate: "2023-04-05",
            likes: 2180,
            downloads: 1650,
            features: ["Advanced physics", "Animation integration", "Input handling", "Cross-platform support"]
          }
        ];
        setFeaturedProducts(placeholderProducts);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Check cart items on component mount
  useEffect(() => {
    // Only set up cart checking if user is authenticated
    if (isAuthenticated) {
      updateCartStatus();

      // Set up interval to check cart status regularly, but only if authenticated
      const intervalId = setInterval(updateCartStatus, 5000); // Increased interval to reduce API calls

      return () => clearInterval(intervalId);
    } else {
      // Clear cart items if not authenticated
      setAddedToCart([]);
    }
  }, [isAuthenticated]); // Re-run when authentication state changes

  const updateCartStatus = () => {
    // Don't make API calls if not authenticated
    if (!isAuthenticated) {
      setAddedToCart([]);
      return;
    }

    getCartItems().then(items => {
      if (items && Array.isArray(items)) {
        const cartItemIds = items.map(item => item.productId);
        setAddedToCart(cartItemIds);
      } else {
        setAddedToCart([]);
      }
    }).catch((error) => {
      console.error('Error fetching cart:', error);
      setAddedToCart([]);
    });
  };

  // Handle add to cart
  const handleAddToCart = async () => {
    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (featuredProducts.length === 0 || activeIndex >= featuredProducts.length) {
      return;
    }

    const currentProduct = featuredProducts[activeIndex];

    // Don't do anything if already adding
    if (isAdding) return;

    // If product is already in cart, navigate to cart
    if (addedToCart.includes(currentProduct.id)) {
      navigate('/cart');
      return;
    }

    setIsAdding(true);

    // Add to cart with a slight delay for animation
    setTimeout(async () => {
      try {
        await addToCart(currentProduct.id);
        updateCartStatus();
      } catch (error) {
        console.error('Error adding to cart:', error);
      } finally {
        setIsAdding(false);
      }
    }, 600);
  };

  // Handle preview
  const handlePreview = () => {
    // Navigate to the product details page
    if (featuredProducts.length === 0 || activeIndex >= featuredProducts.length) {
      return;
    }
    const currentProduct = featuredProducts[activeIndex];
    navigate(`/product/${currentProduct.id}`);
  };

  const nextSlide = () => {
    if (featuredProducts.length === 0) return;
    setActiveIndex((prev) => (prev + 1) % featuredProducts.length);
  };

  const prevSlide = () => {
    if (featuredProducts.length === 0) return;
    setActiveIndex((prev) => (prev - 1 + featuredProducts.length) % featuredProducts.length);
  };

  const startAutoplay = () => {
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
    }

    autoplayTimerRef.current = setInterval(() => {
      nextSlide();
    }, 5000);
  };

  const stopAutoplay = () => {
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (isAutoplay && featuredProducts.length > 0) {
      startAutoplay();
    }

    return () => {
      stopAutoplay();
    };
  }, [isAutoplay, activeIndex, featuredProducts.length]);

  const handleMouseEnter = () => {
    setIsAutoplay(false);
  };

  const handleMouseLeave = () => {
    setIsAutoplay(true);
  };

  // Display loading state
  if (loading) {
    return (
      <section className="py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1B1821] via-[#241F2B] to-[#1B1821]"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#7F56D9] mx-auto"></div>
          <p className="mt-6 text-[#A3A3A3] text-lg">Loading featured products...</p>
        </div>
      </section>
    );
  }

  // Don't show error state, we're using placeholder products instead
  if (featuredProducts.length === 0) {
    return null;
  }

  // Get current product
  const currentProduct = featuredProducts[activeIndex];
  const isInCart = addedToCart.includes(currentProduct.id);

  return (
    <section className="py-12 relative overflow-hidden" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1B1821] via-[#241F2B] to-[#1B1821]"></div>

      {/* Decorative elements */}
      <div className="absolute top-1/4 -left-20 w-60 h-60 rounded-full bg-[#7F56D9]/10 blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#F670C7]/10 blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Image section */}
          <div className="w-full lg:w-1/2 relative overflow-hidden rounded-2xl h-[300px] lg:h-[500px]">
            {featuredProducts.map((product, index) => (
              <div
                key={product.id}
                className={`absolute inset-0 transition-opacity duration-700 ${index === activeIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              >
                <img
                  src={product.images[0]}
                  alt={product.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              </div>
            ))}

            {/* Navigation arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors z-10"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors z-10"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 6L15 12L9 18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {featuredProducts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all ${index === activeIndex ? 'bg-white scale-110' : 'bg-white/50'}`}
                ></button>
              ))}
            </div>
          </div>

          {/* Content section */}
          <div className="w-full lg:w-1/2 flex flex-col">
            <div className="mb-6">
              <h2 className="text-3xl font-bold mb-2">Featured Product</h2>
              <div className="h-1 w-20 bg-gradient-to-r from-[#7F56D9] to-[#F670C7]"></div>
            </div>

            <div className="relative">
              {featuredProducts.map((product, index) => (
                <div
                  key={product.id}
                  className={`transition-all duration-500 ${
                    index === activeIndex
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-8 absolute inset-0 pointer-events-none'
                  }`}
                >
                  <h3 className="text-2xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-[#7F56D9] to-[#F670C7]">
                    {product.title}
                  </h3>
                  <p className="text-[#A3A3A3] mb-4">{product.description}</p>

                  <div className="flex gap-2 mb-4">
                    {product.badges && product.badges.map((badge, i) => (
                      <span key={i} className={`badge ${i === 0 ? 'badge-primary' : i === 1 ? 'badge-secondary' : 'bg-[#342D3F]'}`}>
                        {badge}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#F670C7]"></div>
                      <span>by <strong>{product.sellerName}</strong></span>
                    </div>
                    <div className="text-xl font-bold text-[#F670C7]">${product.price.toFixed(2)}</div>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      variant={isInCart ? "success" : "primary"}
                      size="lg"
                      onClick={handleAddToCart}
                      isLoading={isAdding}
                      leftIcon={isInCart ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11 9h2V6h3V4h-3V1h-2v3H8v2h3v3zm-4 9c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2zm-9.83-3.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.86-7.01L19.42 4h-.01l-1.1 2-2.76 5H8.53l-.13-.27L6.16 6l-.95-2-.94-2H1v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.13 0-.25-.11-.25-.25z" fill="currentColor"/>
                        </svg>
                      )}
                    >
                      {isInCart ? "View Cart" : isAdding ? "Adding..." : "Add to Cart"}
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handlePreview}
                    >
                      Preview
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedSlider;
